import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";

import { ApiError, newULID } from "@/lib/api/client";
import { useDiagnoseGrow } from "@/lib/api/grows";
import { useCreateGrowReading, useLatestReading } from "@/lib/api/grow-readings";
import {
  useCreatePendingAction,
  useDismissPendingActionByID,
} from "@/lib/api/pending-actions";
import type { Grow, GrowDiagnosisAction } from "@/lib/api/types";
import { useLiveStream } from "@/lib/livestream/use-livestream";
import { usePreferences } from "@/lib/preferences";
import {
  ecFromMsCm,
  ecToMsCm,
  tempFromCelsius,
  tempToCelsius,
} from "@/lib/units";
import { numOrUndef } from "@/lib/capture/snapshot";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FormField } from "@/components/forms/field";
import { UrgencyBadge } from "@/components/urgency-badge";

// GrowDiagnosePanel runs the AI "analyze this grow" lookup. Current readings
// are optional and entered in the user's display units (converted to storage
// units on submit). Suggested actions are NOT persisted — each carries an Add
// button that creates a pending action only when the user accepts it.
export function GrowDiagnosePanel({ grow }: { grow: Grow }) {
  const { temperatureUnit, ecUnit } = usePreferences();
  const live = useLiveStream();
  const diagnose = useDiagnoseGrow(grow.id);
  const createAction = useCreatePendingAction();
  const removeAction = useDismissPendingActionByID(grow.id);
  const createReading = useCreateGrowReading(grow.id);

  const [ph, setPh] = useState("");
  const [ec, setEc] = useState("");
  const [temp, setTemp] = useState("");
  const [notes, setNotes] = useState("");
  // Track whether the current readings came straight from the pen (so a saved
  // reading is tagged source="pen" with the sample's own timestamp). Any manual
  // edit flips this back to a hand-entered reading.
  const [penCapture, setPenCapture] = useState<{ at: string } | null>(null);
  const edited = (set: (v: string) => void) => (v: string) => {
    set(v);
    setPenCapture(null);
  };
  // Maps a suggestion TITLE → the pending-action id created when it was added,
  // so an added suggestion can be undone (dismissing that action). Keyed by
  // title (not array index) and intentionally NOT reset on re-analyze, so a
  // re-suggested action still shows as added (no duplicate create) and a
  // create that resolves after a re-analyze can't land on the wrong row.
  const [added, setAdded] = useState<Record<string, string>>({});

  // Reuse-or-rerun (issue 2): the grow's latest saved reading may carry a past
  // analysis. Clicking Analyze then offers to view it instead of spending a new
  // call. `savedView` shows that stored analysis in the result area.
  const latestQ = useLatestReading(grow.id);
  const saved = latestQ.data;
  const savedAnalysis =
    saved && saved.ai_summary
      ? {
          summary: saved.ai_summary,
          actions: saved.ai_actions ?? [],
          llm_call_id: saved.llm_call_id,
          at: saved.captured_at,
        }
      : null;
  const [reuseOpen, setReuseOpen] = useState(false);
  const [savedView, setSavedView] = useState<{
    summary: string;
    actions: GrowDiagnosisAction[];
    llm_call_id?: string;
  } | null>(null);

  // Pull the latest live pen sample into the reading fields (in display units),
  // so a quick measurement → analyze needs no manual typing.
  const sample = live.lastSample;
  const captureFromPen = () => {
    const s = sample?.sample;
    if (!s) return;
    const p = s.ph_filtered ?? s.ph;
    if (typeof p === "number") setPh(p.toFixed(2));
    if (typeof s.ec_ms_per_cm === "number")
      setEc(ecFromMsCm(s.ec_ms_per_cm, ecUnit).toFixed(ecUnit === "ppm700" ? 0 : 2));
    if (typeof s.temperature_c === "number")
      setTemp(tempFromCelsius(s.temperature_c, temperatureUnit).toFixed(1));
    // Stamp the reading with the pen sample's own time so a saved reading sorts
    // correctly relative to refill-session state.
    setPenCapture({ at: new Date(sample!.receivedAt).toISOString() });
  };

  // Build the storage-unit reading payload shared by analyze + save (pH
  // unitless, EC mS/cm, temp °C — converted from the user's display units).
  const readingValues = () => {
    const ecN = numOrUndef(ec);
    const tempN = numOrUndef(temp);
    return {
      ph: numOrUndef(ph),
      ec_ms_per_cm: ecN === undefined ? undefined : ecToMsCm(ecN, ecUnit),
      temp_c: tempN === undefined ? undefined : tempToCelsius(tempN, temperatureUnit),
    };
  };

  const analyze = () => {
    setSavedView(null);
    const v = readingValues();
    diagnose.mutate({
      ph: v.ph,
      ec: v.ec_ms_per_cm,
      temp_c: v.temp_c,
      notes: notes.trim() || undefined,
    });
  };

  // Clicking Analyze: if a past analysis is saved, ask whether to view it or
  // spend a new call; otherwise analyze straight away.
  const onAnalyzeClick = () => {
    if (savedAnalysis && !savedView && !diagnose.data) {
      setReuseOpen(true);
      return;
    }
    analyze();
  };
  const runNew = () => {
    setReuseOpen(false);
    analyze();
  };
  const viewSaved = () => {
    if (!savedAnalysis) return;
    setReuseOpen(false);
    setSavedView({
      summary: savedAnalysis.summary,
      actions: savedAnalysis.actions,
      llm_call_id: savedAnalysis.llm_call_id,
    });
  };

  // Save the current reading to the grow's running log together with the fresh
  // AI assessment (summary + suggested actions + originating llm_call_id), so
  // the log captures the analysis too. captured_at + source reflect a pen
  // capture when present. Only offered after a successful analyze.
  const saveReadingWithAnalysis = () => {
    const v = readingValues();
    const result = diagnose.data;
    if (!result) return;
    createReading.mutate({
      id: newULID(),
      ph: v.ph,
      ec_ms_per_cm: v.ec_ms_per_cm,
      temp_c: v.temp_c,
      source: penCapture ? "pen" : "manual",
      notes: notes.trim() || undefined,
      captured_at: penCapture?.at,
      ai_summary: result.summary,
      ai_actions: result.actions,
      llm_call_id: result.llm_call_id,
    });
  };

  const addAction = (a: GrowDiagnosisAction) => {
    createAction.mutate(
      {
        id: newULID(),
        grow_id: grow.id,
        title: a.title,
        urgency: a.urgency,
        rationale: a.rationale ?? "",
        // Trace AI-created actions back to the call that produced them (whether
        // freshly run or a viewed saved analysis).
        llm_call_id: shown?.llm_call_id,
        // Forward the typed call-to-action so the resulting pending action
        // renders its SPECIFIC primary button + destination. cta_target_id is
        // string|null on the suggestion; the create payload wants string, so
        // null → undefined.
        cta_kind: a.cta_kind,
        cta_target_id: a.cta_target_id ?? undefined,
      },
      {
        onSuccess: (created) =>
          setAdded((m) => ({ ...m, [a.title]: created.id })),
      },
    );
  };

  const removeAdded = (title: string) => {
    const id = added[title];
    if (!id) return;
    removeAction.mutate(
      { id, reason: "removed from grow analysis" },
      {
        onSuccess: () =>
          setAdded((m) => {
            const next = { ...m };
            delete next[title];
            return next;
          }),
      },
    );
  };

  const anyAdded = Object.keys(added).length > 0;

  const err =
    diagnose.error instanceof ApiError
      ? diagnose.error.code === "AI_UNAVAILABLE"
        ? "AI isn’t configured on the server."
        : diagnose.error.message
      : diagnose.error
        ? "Analysis failed."
        : null;

  const result = diagnose.data;
  // The analysis currently on screen: a freshly run diagnosis, or a saved one
  // the grower chose to view from the reuse dialog.
  const shown = savedView ?? result ?? null;

  // Inline save feedback (no global toast system) — a brief "Saved ✓" on
  // success, or the error message on failure.
  const saveStatus = createReading.isError ? (
    <span className="text-xs text-destructive">
      {createReading.error instanceof ApiError
        ? createReading.error.message
        : "Save failed."}
    </span>
  ) : createReading.isSuccess ? (
    <span className="text-xs text-emerald-600 dark:text-emerald-400">
      Saved ✓ — see the grow’s Readings log.
    </span>
  ) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Analyze with AI
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {sample
              ? `pen sample ${formatDistanceToNow(sample.receivedAt, { addSuffix: true })}`
              : "no live pen sample"}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!sample}
            onClick={captureFromPen}
          >
            Capture from pen
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Current pH">
            <Input type="number" step="any" value={ph} onChange={(e) => edited(setPh)(e.target.value)} />
          </FormField>
          <FormField label={`Current EC (${ecUnit === "ppm700" ? "ppm" : "mS/cm"})`}>
            <Input type="number" step="any" value={ec} onChange={(e) => edited(setEc)(e.target.value)} />
          </FormField>
          <FormField label={`Current Temp (${temperatureUnit})`}>
            <Input type="number" step="any" value={temp} onChange={(e) => edited(setTemp)(e.target.value)} />
          </FormField>
        </div>
        <FormField label="Observations (optional)">
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. lower leaves yellowing"
          />
        </FormField>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={diagnose.isPending}
            onClick={onAnalyzeClick}
          >
            {diagnose.isPending ? "Analyzing…" : "✨ Analyze grow"}
          </Button>
          <span className="text-xs text-muted-foreground">
            Runs on the latest saved reading; fields above are optional emphasis.
          </span>
          {saveStatus}
        </div>
        {err && <p className="text-xs text-destructive">{err}</p>}

        {/* Reuse-or-rerun (issue 2): an inline prompt (not a modal — this panel
            can itself live inside a dialog, and nested modals are fragile). */}
        {reuseOpen && (
          <div className="space-y-2 rounded-md border border-dashed p-3">
            <p className="text-sm">
              This grow has an analysis saved
              {savedAnalysis
                ? ` ${formatDistanceToNow(new Date(savedAnalysis.at), { addSuffix: true })}`
                : ""}
              . View it, or run a fresh analysis on the latest data?
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={viewSaved}>
                View saved analysis
              </Button>
              <Button type="button" size="sm" onClick={runNew} disabled={diagnose.isPending}>
                Run new analysis
              </Button>
            </div>
          </div>
        )}

        {shown && (
          <div className="space-y-3">
            {savedView && (
              <p className="text-xs text-muted-foreground">
                Showing the saved analysis
                {savedAnalysis
                  ? ` from ${formatDistanceToNow(new Date(savedAnalysis.at), { addSuffix: true })}`
                  : ""}
                . Click “✨ Analyze grow” for a fresh one.
              </p>
            )}
            <p className="text-sm whitespace-pre-wrap">{shown.summary}</p>
            {/* Save the reading TOGETHER WITH this AI assessment (issue 3) so
                the grow keeps a running log of readings + responses. Only for a
                freshly run analysis — a viewed saved one is already in the log. */}
            {result && !savedView && (
              <Button
                type="button"
                size="sm"
                disabled={createReading.isPending}
                onClick={saveReadingWithAnalysis}
              >
                {createReading.isPending ? "Saving…" : "💾 Save reading + analysis"}
              </Button>
            )}
            {shown.actions.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No actions suggested — looks on target.
              </p>
            ) : (
              <ul className="space-y-2">
                {shown.actions.map((a, i) => (
                  <li
                    key={i}
                    className="flex items-start justify-between gap-3 rounded-md border p-2"
                  >
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <UrgencyBadge urgency={a.urgency} />
                        <span className="text-sm font-medium">{a.title}</span>
                      </div>
                      {a.rationale && (
                        <p className="text-xs text-muted-foreground">
                          {a.rationale}
                        </p>
                      )}
                    </div>
                    {added[a.title] ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={removeAction.isPending}
                        onClick={() => removeAdded(a.title)}
                        title="Remove this action from pending"
                      >
                        Added ✓ · Remove
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={createAction.isPending}
                        onClick={() => addAction(a)}
                      >
                        Add
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {anyAdded && (
              <p className="text-xs text-muted-foreground">
                Added actions show up in{" "}
                <Link
                  to="/pending-actions"
                  className="font-medium text-foreground underline-offset-4 hover:underline"
                >
                  Pending actions →
                </Link>
              </p>
            )}
          </div>
        )}

      </CardContent>
    </Card>
  );
}
